/**
 * Utility functions for working with medication dose times
 */

/**
 * Format time (HH:MM or TIME type) to readable format (e.g., "9:00 AM")
 * @param {string} timeStr - Time string in "HH:MM" or "HH:MM:SS" format
 * @returns {string} Formatted time string (e.g., "9:00 AM")
 */
export const formatTimeToReadable = (timeStr) => {
  if (!timeStr) return '';
  
  // Handle both "HH:MM" and "HH:MM:SS" formats
  const time = timeStr.length >= 5 ? timeStr.substring(0, 5) : timeStr;
  const [hour, minute] = time.split(':').map(Number);
  
  if (isNaN(hour) || isNaN(minute)) return '';
  
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Get time of day label based on hour
 * @param {number} hour - Hour (0-23)
 * @returns {string} Time of day label (Morning, Afternoon, Evening, Night)
 */
export const getTimeOfDay = (hour) => {
  if (hour >= 5 && hour < 12) return 'Morning';
  if (hour >= 12 && hour < 17) return 'Afternoon';
  if (hour >= 17 && hour < 21) return 'Evening';
  return 'Night';
};

/**
 * Convert dose times from medication_doses table to progress items format for Primary mode
 * @param {Array} doses - Array of dose objects from medication_doses table
 * @returns {Array} Progress items array with timeOfDay and status
 */
export const convertDosesToProgressItems = (doses) => {
  if (!doses || doses.length === 0) return [];

  return doses.map(dose => {
    const timeStr = dose.scheduled_time;
    const [hour] = timeStr.split(':').map(Number);

    return {
      timeOfDay: getTimeOfDay(hour),
      status: `Coming at ${formatTimeToReadable(timeStr)}`,
      completed: false
    };
  });
};

/**
 * Convert dose times from medication_doses table to progress items format for Support mode
 * @param {Array} doses - Array of dose objects from medication_doses table
 * @returns {Array} Progress items array with time and status
 */
export const convertDosesToSupportProgress = (doses) => {
  if (!doses || doses.length === 0) return [];

  return doses.map(dose => {
    const timeStr = dose.scheduled_time;
    const [hour] = timeStr.split(':').map(Number);

    return {
      time: getTimeOfDay(hour),
      status: `Pending (${formatTimeToReadable(timeStr)})`,
      completed: false
    };
  });
};

/**
 * Get the current due dose number for a medication
 * @param {Object} medication - Medication object with doses array
 * @param {Date} currentTime - Optional current time (defaults to new Date())
 * @returns {number|null} Current due dose number or null if none due
 */
export const getCurrentDueDose = (medication, currentTime = new Date()) => {
  if (!medication || !medication.doses || medication.doses.length === 0) return null;

  const now = currentTime instanceof Date ? currentTime : new Date();
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  // Find the first dose that is due (scheduled time <= current time)
  for (const dose of medication.doses) {
    if (dose.scheduled_time <= currentTimeStr) {
      return dose.dose_number;
    }
  }

  return null;
};

/**
 * Get the next dose that should be taken for a medication, considering taken doses
 * @param {Object} medication - Medication object with doses array
 * @param {Array} medicationLogs - Array of medication log objects from today
 * @param {Date} currentTime - Optional current time (defaults to new Date())
 * @returns {number|null} Dose number of the next dose to take, or null if none
 */
export const getNextDueDose = (medication, medicationLogs = [], currentTime = new Date()) => {
  if (!medication || !medication.doses || medication.doses.length === 0) return null;

  // Get dose statuses to see which doses are taken and can be taken
  const doseStatuses = getDoseStatus(medication, medicationLogs, currentTime);

  // Find the first dose that can be taken now (due, late, or upcoming) and not yet taken
  for (const dose of medication.doses) {
    const doseStatus = doseStatuses.find(s => s.dose_number === dose.dose_number);
    if (!doseStatus?.isTaken && doseStatus?.canTakeNow) {
      return dose.dose_number;
    }
  }

  // If no doses can be taken now, find the next dose that will be takeable
  // (This could be an upcoming dose that becomes due, or a late dose that becomes available)
  for (const dose of medication.doses) {
    const doseStatus = doseStatuses.find(s => s.dose_number === dose.dose_number);
    if (!doseStatus?.isTaken && !doseStatus?.isMissed) {
      return dose.dose_number;
    }
  }

  return null;
};

/**
 * Check if a specific dose was taken today
 * @param {Object} medication - Medication object
 * @param {number} doseNumber - Dose number to check
 * @param {Array} medicationLogs - Array of medication log objects from today
 * @returns {boolean} True if dose was taken today
 */
export const isDoseTakenToday = (medication, doseNumber, medicationLogs) => {
  if (!medication || !medicationLogs) return false;

  return medicationLogs.some(log =>
    log.medication_id === medication.id && log.dose_number === doseNumber
  );
};

/**
 * Get dose status for all doses of a medication using medication-specific guidance rules
 * @param {Object} medication - Medication object with doses array and missedDoseGuidance
 * @param {Array} medicationLogs - Array of medication log objects from today
 * @param {Date} currentTime - Optional current time (defaults to new Date())
 * @returns {Array} Array of dose status objects with enhanced status information
 */
export const getDoseStatus = (medication, medicationLogs = [], currentTime = new Date()) => {
  if (!medication || !medication.doses || medication.doses.length === 0) return [];

  const now = currentTime instanceof Date ? currentTime : new Date();
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return medication.doses.map(dose => {
    const isTaken = isDoseTakenToday(medication, dose.dose_number, medicationLogs);

    if (isTaken) {
      return {
        dose_number: dose.dose_number,
        scheduled_time: dose.scheduled_time,
        isTaken: true,
        isDue: false,
        isLate: false,
        isMissed: false,
        isUpcoming: false,
        canTakeNow: false,
        guidanceMessage: 'Dose taken successfully'
      };
    }

    // Create scheduledDateTime based on currentTime to preserve the date
    const scheduledDateTime = new Date(now);
    const [hours, minutes] = dose.scheduled_time.split(':').map(Number);
    const currentDateTime = new Date(now);

    // Set scheduled time to TODAY at the specified time
    // We're checking if TODAY's scheduled dose was taken, is due, is late, or is missed
    scheduledDateTime.setHours(hours, minutes, 0, 0);
    
    // Special handling for midnight doses (00:00)
    // For midnight, we treat it as today's midnight (00:00 today)
    // If it's currently past midnight, today's midnight is in the past
    if (hours === 0 && minutes === 0) {
      // Already set to today at 00:00
      // If current time is past midnight, this dose is in the past (missed/late if not taken)
      // If current time is exactly 00:00, this dose is due now
    }

    // Calculate time difference from today's scheduled time
    // Positive = scheduled time is in the past (missed/late if not taken)
    // Negative = scheduled time is in the future (upcoming)
    const timeDiffHours = (currentDateTime - scheduledDateTime) / (1000 * 60 * 60);

    // Determine frequency based on number of doses per day
    const numDoses = medication.doses.length;
    const isOnceOrTwiceDaily = numDoses <= 2;
    const isMoreThanTwiceDaily = numDoses > 2;

    // Check if this is the first dose (earliest scheduled dose of the day)
    const sortedDoses = [...medication.doses].sort((a, b) => 
      a.scheduled_time.localeCompare(b.scheduled_time)
    );
    const isFirstDose = dose.dose_number === sortedDoses[0].dose_number;

    // Find the next scheduled dose (could be later today or tomorrow)
    const nextDose = medication.doses
      .filter(d => d.dose_number !== dose.dose_number && d.scheduled_time > dose.scheduled_time)
      .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))[0] ||
      medication.doses
        .filter(d => d.dose_number !== dose.dose_number)
        .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))[0];

    // Calculate time until next dose
    let timeToNextDose = null;
    if (nextDose) {
      const nextDoseDateTime = new Date(now);
      const [nextHours, nextMinutes] = nextDose.scheduled_time.split(':').map(Number);
      nextDoseDateTime.setHours(nextHours, nextMinutes, 0, 0);
      
      // If next dose time is earlier than current time, it's tomorrow's dose
      if (nextDoseDateTime < currentDateTime) {
        nextDoseDateTime.setDate(nextDoseDateTime.getDate() + 1);
      }
      
      timeToNextDose = (nextDoseDateTime - currentDateTime) / (1000 * 60 * 60);
    }

    // Determine status based on medication guidance rules
    const guidance = medication.missed_dose_guidance || {
      type: 'time_based',
      rules: { if_remembered_within_hours: 12 },
      doubleDosingRule: 'never_double',
      minimumInterval_hours: 12,
      criticalMedication: false
    };

    let isDue = false;
    let isLate = false;
    let isMissed = false;
    let isUpcoming = false;
    let canTakeNow = false;
    let guidanceMessage = '';

    if (timeDiffHours < 0) {
      // Future dose
      isUpcoming = true;
      canTakeNow = false;
      guidanceMessage = `Next dose at ${formatTimeToReadable(dose.scheduled_time)}`;
    } else if (timeDiffHours >= 0) {
      // Past or current dose time
      // Rule: Never take more than one dose at a time to make up for a forgotten/late dose
      
      const twoHourThreshold = 2; // 2 hours is the threshold for "late" vs "too late"
      
      if (timeDiffHours <= twoHourThreshold) {
        // Less than 2 hours late - may be taken
        isDue = timeDiffHours <= 0.5; // Within 30 minutes is "due"
        isLate = timeDiffHours > 0.5 && timeDiffHours <= twoHourThreshold;
        canTakeNow = true;
        guidanceMessage = isDue
          ? 'Take now - on time'
          : `Take now - ${Math.round(timeDiffHours * 10) / 10} hours late`;
      } else {
        // More than 2 hours late - depends on medication frequency
        if (isOnceOrTwiceDaily) {
          // 1-2 doses per day: Can take missed dose if next dose is not due within 3 hours
          const nextDoseThreshold = 3; // Hours
          
          if (timeToNextDose !== null && timeToNextDose <= nextDoseThreshold) {
            // Next dose is due within 3 hours - do not take this dose
            isMissed = true;
            canTakeNow = false;
            guidanceMessage = `Skip - next dose due in ${Math.round(timeToNextDose)} hours`;
          } else {
            // Next dose is not due soon - can take the missed dose
            isLate = true;
            canTakeNow = true;
            guidanceMessage = `Take now - ${Math.round(timeDiffHours * 10) / 10} hours late`;
          }
        } else if (isMoreThanTwiceDaily) {
          // More than twice a day: Do not take the dose, wait until next dose is due
          isMissed = true;
          canTakeNow = false;
          if (timeToNextDose !== null) {
            guidanceMessage = `Skip missed dose - next dose at ${formatTimeToReadable(nextDose.scheduled_time)}`;
          } else {
            guidanceMessage = 'Skip missed dose - wait until next scheduled dose';
          }
        } else if (guidance.type === 'time_based') {
          const gracePeriodHours = guidance.rules?.if_remembered_within_hours || 12;
          if (timeDiffHours <= gracePeriodHours) {
            isDue = timeDiffHours <= 1; // Within 1 hour is "due"
            isLate = timeDiffHours > 1 && timeDiffHours <= gracePeriodHours;
            canTakeNow = true;
            guidanceMessage = isDue
              ? 'Take now - on time'
              : `Take now - ${Math.round(timeDiffHours)} hours late`;
          } else {
            isMissed = true;
            canTakeNow = false;
            guidanceMessage = 'Too late - skip and take next dose on time';
          }
        } else if (guidance.type === 'proximity_based') {
          const thresholdHours = guidance.rules?.next_dose_threshold_hours || 4;
          
          // Special handling for midnight doses (00:00)
          // For midnight doses, check time until the next upcoming dose
          const isMidnightDose = hours === 0 && minutes === 0;
          
          if (isMidnightDose) {
            // Find the next upcoming dose (could be today or tomorrow)
            // First, try to find a dose later today
            const nextDoseToday = medication.doses
              .filter(d => d.dose_number !== dose.dose_number && d.scheduled_time !== '00:00:00')
              .find(d => {
                const [dHours, dMinutes] = d.scheduled_time.split(':').map(Number);
                const doseTime = new Date(now);
                doseTime.setHours(dHours, dMinutes, 0, 0);
                return doseTime > currentDateTime;
              });
            
            // If no dose later today, find the first dose tomorrow
            const nextDose = nextDoseToday || medication.doses
              .filter(d => d.dose_number !== dose.dose_number && d.scheduled_time !== '00:00:00')
              .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))[0];
            
            if (nextDose) {
              const nextDoseDateTime = new Date(now);
              const [nextHours, nextMinutes] = nextDose.scheduled_time.split(':').map(Number);
              nextDoseDateTime.setHours(nextHours, nextMinutes, 0, 0);
              
              // If next dose is earlier today, it's tomorrow's first dose
              if (nextDoseDateTime < currentDateTime) {
                nextDoseDateTime.setDate(nextDoseDateTime.getDate() + 1);
              }
              
              const timeToNextDose = (nextDoseDateTime - currentDateTime) / (1000 * 60 * 60);
              
              // For midnight dose with proximity-based guidance:
              // Allow taking if we're more than threshold hours before next dose
              // AND we're within a reasonable window (12 hours) of midnight
              if (timeToNextDose > thresholdHours && timeDiffHours <= 12) {
                isDue = timeDiffHours <= 1;
                isLate = timeDiffHours > 1 && timeDiffHours <= 12;
                canTakeNow = true;
                guidanceMessage = `Take now - ${Math.round(timeToNextDose)} hours until next dose`;
              } else if (timeToNextDose <= thresholdHours) {
                // Too close to next dose
                isMissed = true;
                canTakeNow = false;
                guidanceMessage = `Skip - only ${Math.round(timeToNextDose)} hours until next dose`;
              } else {
                // Too late (more than 12 hours after midnight)
                isMissed = true;
                canTakeNow = false;
                guidanceMessage = 'Too late - skip and take next dose on time';
              }
            } else {
              // No other doses, treat as time-based
              const gracePeriodHours = 12;
              if (timeDiffHours <= gracePeriodHours) {
                isDue = timeDiffHours <= 1;
                isLate = timeDiffHours > 1 && timeDiffHours <= gracePeriodHours;
                canTakeNow = true;
                guidanceMessage = isDue ? 'Take now - on time' : 'Take now if remembered';
              } else {
                isMissed = true;
                canTakeNow = false;
                guidanceMessage = 'Too late - skip and resume tomorrow';
              }
            }
          } else {
            // Regular proximity-based logic for non-midnight doses
            const nextDose = medication.doses.find(d =>
              d.dose_number !== dose.dose_number &&
              d.scheduled_time > dose.scheduled_time
            ) || medication.doses.find(d => d.dose_number !== dose.dose_number);

            if (nextDose) {
              const nextDoseDateTime = new Date(now);
              const [nextHours, nextMinutes] = nextDose.scheduled_time.split(':').map(Number);
              nextDoseDateTime.setHours(nextHours, nextMinutes, 0, 0);

              // If next dose time is earlier than current time, it's tomorrow's dose
              if (nextDoseDateTime < currentDateTime) {
                nextDoseDateTime.setDate(nextDoseDateTime.getDate() + 1);
              }

              const timeToNextDose = (nextDoseDateTime - currentDateTime) / (1000 * 60 * 60);

              if (timeToNextDose > thresholdHours) {
                isDue = timeDiffHours <= 1;
                isLate = timeDiffHours > 1 && timeDiffHours <= thresholdHours * 2;
                canTakeNow = true;
                guidanceMessage = `Take now - ${Math.round(timeToNextDose)} hours until next dose`;
              } else {
                isMissed = true;
                canTakeNow = false;
                guidanceMessage = `Skip - only ${Math.round(timeToNextDose)} hours until next dose`;
              }
            } else {
              // Fallback to time-based for last dose of day
              const gracePeriodHours = 12;
              if (timeDiffHours <= gracePeriodHours) {
                isDue = timeDiffHours <= 1;
                isLate = timeDiffHours > 1 && timeDiffHours <= gracePeriodHours;
                canTakeNow = true;
                guidanceMessage = isDue ? 'Take now - on time' : 'Take now if remembered';
              } else {
                isMissed = true;
                canTakeNow = false;
                guidanceMessage = 'Too late - skip and resume tomorrow';
              }
            }
          }
        } else if (guidance.type === 'skip_based') {
          isMissed = timeDiffHours > 0;
          canTakeNow = false;
          guidanceMessage = 'Do not take late - resume with next scheduled dose';
        } else {
          // Complex/consult - mark as missed and require consultation
          isMissed = timeDiffHours > 0;
          canTakeNow = false;
          guidanceMessage = 'Contact healthcare provider for guidance';
        }
      }
    }

    return {
      dose_number: dose.dose_number,
      scheduled_time: dose.scheduled_time,
      isTaken,
      isDue,
      isLate,
      isMissed,
      isUpcoming,
      canTakeNow,
      guidanceMessage,
      timeDiffHours: Math.round(timeDiffHours * 10) / 10 // Round to 1 decimal
    };
  });
};

